import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';

@Component({
    selector: 'app-company-info-edit-modal',
    templateUrl: './company-info-edit-modal.component.html',
    styleUrls: ['./company-info-edit-modal.component.css'],
    standalone: false
})
export class CompanyInfoEditModalComponent implements OnInit, OnChanges {
    @Input() isOpen = false;
    @Input() existingCompanyInfo: any = null;
    @Output() close = new EventEmitter<void>();
    @Output() save = new EventEmitter<any>();

    companyForm: UntypedFormGroup;
    submitted = false;

    constructor(private formBuilder: UntypedFormBuilder) {
        this.companyForm = this.formBuilder.group({
            // Company Identity
            companyName: new UntypedFormControl('', [
                Validators.required,
                Validators.minLength(2)
            ]),

            // Legal Information
            legalInfo: this.formBuilder.group({
                rccm: new UntypedFormControl(''),
                niu: new UntypedFormControl(''),
                businessLicenseNumber: new UntypedFormControl('')
            }),

            // Legal Representative
            legalRepresentative: this.formBuilder.group({
                fullName: new UntypedFormControl(''),
                position: new UntypedFormControl(''),
                phoneNumber: new UntypedFormControl('', Validators.pattern(/^6[5-9]\d{7}$/))
            }),

            // Operational Information
            operationalInfo: this.formBuilder.group({
                companyEmail: new UntypedFormControl('', Validators.email),
                companyPhoneNumber: new UntypedFormControl('', Validators.pattern(/^6[5-9]\d{7}$/)),
                country: new UntypedFormControl(''),
                city: new UntypedFormControl(''),
                address: new UntypedFormControl(''),
                description: new UntypedFormControl('')
            })
        });
    }

    ngOnInit(): void {
        // Listen for legal info changes to update validators
        this.companyForm.get('legalInfo')?.valueChanges.subscribe(() => {
            this.updateLegalInfoValidators();
        });

        // Listen for legal representative changes to update validators
        this.companyForm.get('legalRepresentative')?.valueChanges.subscribe(() => {
            this.updateLegalRepresentativeValidators();
        });

        // Patch form if data is already available on init
        if (this.existingCompanyInfo) {
            this.patchFormValues();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        // When isOpen changes to true OR existingCompanyInfo changes, patch the form
        const isOpenChange = changes['isOpen'];
        const infoChange = changes['existingCompanyInfo'];

        if (isOpenChange && isOpenChange.currentValue === true && this.existingCompanyInfo) {
            // Modal just opened — always re-patch so form is fresh
            this.patchFormValues();
        } else if (infoChange && infoChange.currentValue) {
            // Data arrived/changed while modal is already open
            this.patchFormValues();
        }
    }

    private patchFormValues(): void {
        // Reset form first to clear any previous values
        this.companyForm.reset();
        this.submitted = false;

        // API response uses legalRepresentative (proper spelling)
        this.companyForm.patchValue({
            companyName: this.existingCompanyInfo.companyName || '',
            legalInfo: {
                rccm: this.existingCompanyInfo.legalInfo?.rccm || '',
                niu: this.existingCompanyInfo.legalInfo?.niu || '',
                businessLicenseNumber: this.existingCompanyInfo.legalInfo?.businessLicenseNumber || ''
            },
            legalRepresentative: {
                fullName: this.existingCompanyInfo.legalRepresentative?.fullName || '',
                position: this.existingCompanyInfo.legalRepresentative?.position || '',
                phoneNumber: this.existingCompanyInfo.legalRepresentative?.phoneNumber || ''
            },
            operationalInfo: {
                companyEmail: this.existingCompanyInfo.operationalInfo?.companyEmail || '',
                companyPhoneNumber: this.existingCompanyInfo.operationalInfo?.companyPhoneNumber || '',
                country: this.existingCompanyInfo.operationalInfo?.country || '',
                city: this.existingCompanyInfo.operationalInfo?.city || '',
                address: this.existingCompanyInfo.operationalInfo?.address || '',
                description: this.existingCompanyInfo.operationalInfo?.description || ''
            }
        });
    }

    get f() {
        return this.companyForm.controls;
    }

    private updateLegalInfoValidators(): void {
        const legalInfoGroup = this.companyForm.get('legalInfo');
        const rccmControl = legalInfoGroup?.get('rccm');
        const niuControl = legalInfoGroup?.get('niu');
        const businessLicenseControl = legalInfoGroup?.get('businessLicenseNumber');

        const hasAnyValue = rccmControl?.value || niuControl?.value || businessLicenseControl?.value;

        if (hasAnyValue) {
            rccmControl?.setValidators(Validators.required);
            niuControl?.setValidators(Validators.required);
            businessLicenseControl?.setValidators(Validators.required);
        } else {
            rccmControl?.clearValidators();
            niuControl?.clearValidators();
            businessLicenseControl?.clearValidators();
        }

        rccmControl?.updateValueAndValidity({ emitEvent: false });
        niuControl?.updateValueAndValidity({ emitEvent: false });
        businessLicenseControl?.updateValueAndValidity({ emitEvent: false });
    }

    private updateLegalRepresentativeValidators(): void {
        const repGroup = this.companyForm.get('legalRepresentative');
        const fullNameControl = repGroup?.get('fullName');
        const positionControl = repGroup?.get('position');
        const phoneControl = repGroup?.get('phoneNumber');

        const hasAnyValue = fullNameControl?.value || positionControl?.value || phoneControl?.value;

        if (hasAnyValue) {
            fullNameControl?.setValidators([Validators.required, Validators.minLength(2)]);
            positionControl?.setValidators([Validators.required, Validators.minLength(2)]);
            phoneControl?.setValidators([
                Validators.required,
                Validators.pattern(/^6[5-9]\d{7}$/)
            ]);
        } else {
            fullNameControl?.clearValidators();
            positionControl?.clearValidators();
            phoneControl?.setValidators(Validators.pattern(/^6[5-9]\d{7}$/));
        }

        fullNameControl?.updateValueAndValidity({ emitEvent: false });
        positionControl?.updateValueAndValidity({ emitEvent: false });
        phoneControl?.updateValueAndValidity({ emitEvent: false });
    }

    onSubmit(): void {
        this.submitted = true;

        if (this.companyForm.invalid) {
            return;
        }

        const formValue = this.companyForm.value;

        const companyData = {
            companyName: formValue.companyName,
            legalInfo: {
                rccm: formValue.legalInfo.rccm || null,
                niu: formValue.legalInfo.niu || null,
                businessLicenseNumber: formValue.legalInfo.businessLicenseNumber || null
            },
            legalRepresentative: {
                fullName: formValue.legalRepresentative.fullName || null,
                position: formValue.legalRepresentative.position || null,
                phoneNumber: formValue.legalRepresentative.phoneNumber || null
            },
            operationalInfo: {
                companyEmail: formValue.operationalInfo.companyEmail || null,
                companyPhoneNumber: formValue.operationalInfo.companyPhoneNumber || null,
                country: formValue.operationalInfo.country || null,
                city: formValue.operationalInfo.city || null,
                address: formValue.operationalInfo.address || null,
                description: formValue.operationalInfo.description || null
            }
        };

        this.save.emit(companyData);
        this.submitted = false;
    }

    onClose(): void {
        this.submitted = false;
        this.companyForm.reset();
        this.close.emit();
    }

    onBackdropClick(event: MouseEvent): void {
        // Only close if clicking the outer modal wrapper (the backdrop), not its children
        if ((event.target as HTMLElement) === event.currentTarget) {
            this.onClose();
        }
    }
}